/**
 * CustomNodeStorage Service
 * Handles persistence of custom node definitions using localStorage
 */

import type { CustomNodeDefinition } from '../types/customNode';
import logger from '../utils/logger';

class CustomNodeStorage {
  private static STORAGE_KEY = 'creative-nodeflow-custom-nodes';
  private static VERSION_KEY = 'creative-nodeflow-custom-nodes-version';
  private static CURRENT_VERSION = '1.0';

  /**
   * Get all custom node definitions
   */
  static getAll(): CustomNodeDefinition[] {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      if (!data) {
        return [];
      }

      const nodes = JSON.parse(data);

      // Validate data structure
      if (!Array.isArray(nodes)) {
        logger.warn('[CustomNodeStorage] Invalid data structure, resetting');
        return [];
      }

      return nodes;
    } catch (error) {
      logger.error('[CustomNodeStorage] Failed to load custom nodes:', error);
      return [];
    }
  }

  /**
   * Save a custom node definition (create or update)
   */
  static save(definition: CustomNodeDefinition): void {
    try {
      const nodes = this.getAll();
      const existingIndex = nodes.findIndex(n => n.id === definition.id);

      const now = new Date().toISOString();

      if (existingIndex >= 0) {
        // Update existing
        nodes[existingIndex] = {
          ...definition,
          updatedAt: now
        };
        logger.info('[CustomNodeStorage] Updated custom node:', definition.id);
      } else {
        // Create new
        nodes.push({
          ...definition,
          createdAt: now,
          updatedAt: now
        });
        logger.info('[CustomNodeStorage] Created custom node:', definition.id);
      }

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(nodes));
      localStorage.setItem(this.VERSION_KEY, this.CURRENT_VERSION);
    } catch (error) {
      logger.error('[CustomNodeStorage] Failed to save custom node:', error);
      throw new Error('Failed to save custom node');
    }
  }

  /**
   * Delete a custom node definition
   */
  static delete(id: string): void {
    try {
      const nodes = this.getAll().filter(n => n.id !== id);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(nodes));
      logger.info('[CustomNodeStorage] Deleted custom node:', id);
    } catch (error) {
      logger.error('[CustomNodeStorage] Failed to delete custom node:', error);
      throw new Error('Failed to delete custom node');
    }
  }

  /**
   * Get a custom node definition by ID
   */
  static getById(id: string): CustomNodeDefinition | undefined {
    return this.getAll().find(n => n.id === id);
  }

  /**
   * Check if a custom node with the given name already exists
   */
  static existsByName(name: string, excludeId?: string): boolean {
    const nodes = this.getAll();
    return nodes.some(n =>
      n.name.toLowerCase() === name.toLowerCase() &&
      n.id !== excludeId
    );
  }

  /**
   * Clear all custom nodes (useful for testing or reset)
   */
  static clear(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem(this.VERSION_KEY);
      logger.info('[CustomNodeStorage] Cleared all custom nodes');
    } catch (error) {
      logger.error('[CustomNodeStorage] Failed to clear custom nodes:', error);
    }
  }

  /**
   * Export custom nodes as JSON
   */
  static export(): string {
    const nodes = this.getAll();
    return JSON.stringify(nodes, null, 2);
  }

  /**
   * Import custom nodes from JSON
   */
  static import(jsonString: string): { success: number; failed: number } {
    try {
      const importedNodes = JSON.parse(jsonString);

      if (!Array.isArray(importedNodes)) {
        throw new Error('Invalid import format: expected array');
      }

      let success = 0;
      let failed = 0;

      importedNodes.forEach(node => {
        try {
          // Validate required fields
          if (!node.id || !node.name || !node.promptTemplate) {
            throw new Error('Missing required fields');
          }

          this.save(node as CustomNodeDefinition);
          success++;
        } catch (error) {
          logger.error('[CustomNodeStorage] Failed to import node:', error);
          failed++;
        }
      });

      logger.info(`[CustomNodeStorage] Import complete: ${success} succeeded, ${failed} failed`);
      return { success, failed };
    } catch (error) {
      logger.error('[CustomNodeStorage] Failed to import custom nodes:', error);
      throw new Error('Failed to import custom nodes');
    }
  }

  /**
   * Get storage statistics
   */
  static getStats(): {
    totalNodes: number;
    storageSize: number;
    version: string;
  } {
    const nodes = this.getAll();
    const data = localStorage.getItem(this.STORAGE_KEY) || '';

    return {
      totalNodes: nodes.length,
      storageSize: new Blob([data]).size,
      version: localStorage.getItem(this.VERSION_KEY) || 'unknown'
    };
  }
}

export default CustomNodeStorage;
